
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/providers/auth-provider';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { startOfDayQatar, endOfDayQatar, getQatarDayRange, getLastQatarMonthRange, getQatarRangeFromDates, getTodayQatar, getNDaysAgoQatar } from '@/utils/timezone';
import {
  calculateSessionSeconds,
  formatDurationFromSeconds,
  getReportableTimeLogIntervals,
  getLastScreenshotByTimeLogId,
} from '@/lib/time-utils';
import { calculatePayableTimeSummary, type PayableTimeSummary } from '@/lib/payable-time';
import { fetchPaginated } from '@/lib/supabase-utils';
import { fetchSessionScreenshotEvidence } from '@/lib/screenshot-evidence';
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { fetchProjects as fetchProjectsService } from '@/domains/people';
import { Calendar, Clock, Download, Filter, Search, RefreshCw, Activity, Camera } from 'lucide-react';

interface TimeLog {
  id: string;
  start_time: string;
  end_time: string | null;
  user_id: string;
  project_id: string;
  deducted_seconds?: number | null;
  status?: string | null;
  duration_seconds?: number;
  projects: {
    name: string;
  };
}

interface RawTimeLog extends Omit<TimeLog, 'duration_seconds' | 'projects'> {
  idle_seconds: number | null;
  projects: { name: string } | null;
}

interface ScreenshotSummary {
  user_id: string | null;
  time_log_id: string | null;
  captured_at: string;
  image_deleted_at: string | null;
  activity_percent: number | null;
}

interface IdleLogSummary {
  idle_start: string;
  idle_end: string | null;
}

interface Project {
  id: string;
  name: string;
}

export default function EmployeeReports() {
  const { userDetails, user, isSuperAdmin } = useAuth();
  const [timeLogs, setTimeLogs] = useState<TimeLog[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>();
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>();
  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  // Summary stats
  const [summaryScreenshots, setSummaryScreenshots] = useState<{ activity_percent: number | null }[]>([]);
  const [timeSummary, setTimeSummary] = useState<PayableTimeSummary>({
    trackedSeconds: 0,
    idleSeconds: 0,
    payableSeconds: 0,
  });

  // Organization filtering
  const organizationId = userDetails?.organization_id;

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    fetchTimeLogs();
    
    // Add real-time auto-refresh every 30 seconds for live session updates
    const interval = setInterval(() => {
      fetchTimeLogs();
    }, 30000); // 30 seconds - fast enough to show new sessions quickly
    
    return () => clearInterval(interval);
  }, [dateRange, searchTerm, customStartDate, customEndDate]);

  // Additional useEffect for immediate refresh on focus (when user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      fetchTimeLogs();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await fetchProjectsService({ organizationId, isSuperAdmin });
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchTimeLogs = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { start: rangeStartISO, end: rangeEndISO } = getDateRangeQatar();
      const rangeStart = new Date(rangeStartISO);
      const rangeEnd = new Date(rangeEndISO);

      // Time logs that overlap the selected Qatar-aligned range.
      let tlQuery = supabase
        .from('time_logs')
        .select(`id, start_time, end_time, user_id, project_id, idle_seconds, deducted_seconds, status, projects (name)`)
        .eq('user_id', user.id)
        .lt('start_time', rangeEnd.toISOString())
        .or(`end_time.gte.${rangeStart.toISOString()},end_time.is.null`)
        .order('start_time', { ascending: false });

      if (organizationId && !isSuperAdmin) tlQuery = tlQuery.eq('organization_id', organizationId);

      const { data: tlData, error: tlError } = await tlQuery;
      if (tlError) throw tlError;

      const rawTimeLogs = (tlData || []) as unknown as RawTimeLog[];
      const unique = rawTimeLogs.filter((log, index, logs) => logs.findIndex(candidate => candidate.id === log.id) === index);
      const rangeStartMs = rangeStart.getTime();
      const rangeEndMs = rangeEnd.getTime();
      const nowMs = Date.now();

      const [screenshotsData, idleLogsData, sessionEvidence] = await Promise.all([
        fetchPaginated<ScreenshotSummary>(
          supabase
            .from('screenshots')
            .select('user_id, time_log_id, captured_at, image_deleted_at, activity_percent')
            .eq('user_id', user.id)
            .gte('captured_at', rangeStart.toISOString())
            .lt('captured_at', rangeEnd.toISOString())
            .order('captured_at')
        ),
        fetchPaginated<IdleLogSummary>(
          supabase
            .from('idle_logs')
            .select('idle_start, idle_end')
            .eq('user_id', user.id)
            .lt('idle_start', rangeEnd.toISOString())
            .or(`idle_end.gte.${rangeStart.toISOString()},idle_end.is.null`)
            .order('idle_start')
        ),
        fetchSessionScreenshotEvidence(supabase, unique, { rangeStartMs, rangeEndMs, nowMs }),
      ]);

      const screenshotsBySession = getLastScreenshotByTimeLogId(unique, sessionEvidence, {
        rangeStartMs, rangeEndMs, nowMs,
      });

      const reportableIntervals = getReportableTimeLogIntervals(
        unique.map(log => ({
          id: log.id,
          start_time: log.start_time,
          end_time: log.end_time,
          deducted_seconds: log.deducted_seconds,
          status: log.status,
          user_id: log.user_id,
        })),
        { rangeStartMs, rangeEndMs, nowMs, screenshotsBySession }
      );
      const durationByLogId = new Map<string, number>();
      reportableIntervals.forEach(interval => {
        if (!interval.logId) return;
        durationByLogId.set(
          interval.logId,
          (durationByLogId.get(interval.logId) || 0) + Math.floor((interval.endMs - interval.startMs) / 1000)
        );
      });

      const transformedLogs: TimeLog[] = unique
        .filter(log => durationByLogId.has(log.id))
        .map(log => ({
          id: log.id,
          start_time: log.start_time,
          end_time: log.end_time,
          user_id: log.user_id,
          project_id: log.project_id,
          deducted_seconds: log.deducted_seconds,
          status: log.status,
          duration_seconds: durationByLogId.get(log.id) || 0,
          projects: { name: log.projects?.name || 'Unknown Project' },
        }))
        .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

      setTimeLogs(transformedLogs);
      setTimeSummary(calculatePayableTimeSummary(
        reportableIntervals,
        idleLogsData,
        {
          rangeStartMs,
          rangeEndMs,
          nowMs,
          fallbackTimeLogs: unique,
        }
      ));
      setSummaryScreenshots(screenshotsData as { activity_percent: number | null }[]);

      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching time logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeQatar = (): { start: string; end: string } => {
    const todayStr = getTodayQatar();
    switch (dateRange) {
      case 'today':
        return getQatarDayRange(todayStr);
      case 'week': {
        const startStr = getNDaysAgoQatar(7);
        return { start: getQatarDayRange(startStr).start, end: getQatarDayRange(todayStr).end };
      }
      case 'month': {
        const startStr = getNDaysAgoQatar(30);
        return { start: getQatarDayRange(startStr).start, end: getQatarDayRange(todayStr).end };
      }
      case 'last-month':
        return getLastQatarMonthRange();
      case 'custom':
        if (customStartDate && customEndDate) {
          return getQatarRangeFromDates(customStartDate, customEndDate);
        }
        return getQatarDayRange(todayStr);
      default:
        return getQatarDayRange(todayStr);
    }
  };

  // Summary derived values
  const summaryTotalScreenshots = summaryScreenshots.length;
  const actValues = summaryScreenshots.filter(s => s.activity_percent != null).map(s => s.activity_percent as number);
  const summaryAvgActivity = actValues.length > 0 ? Math.round(actValues.reduce((a, b) => a + b, 0) / actValues.length) : null;
  const formatSummaryDuration = (seconds: number) => seconds > 0 ? formatDurationFromSeconds(seconds) : '0m';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Time Reports</h1>
          <p className="text-muted-foreground">Your tracked time and activity</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <div className="flex items-center text-sm text-muted-foreground">
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Auto-refresh: 30s'}
            </div>
            <div className="text-xs text-muted-foreground">
              Last updated: {format(lastRefresh, 'HH:mm:ss')}
            </div>
          </div>
          <Button onClick={fetchTimeLogs} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Reports</CardTitle>
          <CardDescription>View your time logs and project allocations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select value={dateRange} onValueChange={(value) => {
              setDateRange(value);
              if (value === 'custom') setCustomPickerOpen(true);
            }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Last 7 days</SelectItem>
                <SelectItem value="month">Last 30 days</SelectItem>
                <SelectItem value="last-month">Last Month</SelectItem>
                <SelectItem value="custom">Custom Dates</SelectItem>
              </SelectContent>
            </Select>
            {dateRange === 'custom' && (
              <Popover open={customPickerOpen} onOpenChange={setCustomPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Calendar className="h-3.5 w-3.5" />
                    {customStartDate && customEndDate
                      ? `${format(customStartDate, 'MMM d')} - ${format(customEndDate, 'MMM d, yyyy')}`
                      : 'Pick dates'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-4" align="start">
                  <div className="flex gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Start Date</Label>
                      <CalendarPicker mode="single" selected={customStartDate} onSelect={(date) => { setCustomStartDate(date); if (date && customEndDate && date > customEndDate) setCustomEndDate(undefined); }} initialFocus />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">End Date</Label>
                      <CalendarPicker mode="single" selected={customEndDate} onSelect={(date) => { setCustomEndDate(date); if (date && customStartDate) setCustomPickerOpen(false); }} disabled={(date) => customStartDate ? date < customStartDate : false} />
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}

            <div className="flex items-center space-x-2">
              <Input
                type="text"
                placeholder="Search by project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          {/* Summary stats cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
            <Card className="border shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="h-5 w-5 text-blue-500 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Payable Hours</p>
                  <p className="text-lg font-bold">{formatSummaryDuration(timeSummary.payableSeconds)}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="h-5 w-5 text-sky-400 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Tracked Hours</p>
                  <p className="text-lg font-bold">{formatSummaryDuration(timeSummary.trackedSeconds)}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <RefreshCw className="h-5 w-5 text-orange-400 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Idle Time</p>
                  <p className="text-lg font-bold">{formatSummaryDuration(timeSummary.idleSeconds)}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <Activity className="h-5 w-5 text-green-500 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Avg Activity</p>
                  <p className="text-lg font-bold">{summaryAvgActivity != null ? `${summaryAvgActivity}%` : '—'}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardContent className="p-4 flex items-center gap-3">
                <Camera className="h-5 w-5 text-purple-500 shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Screenshots</p>
                  <p className="text-lg font-bold">{summaryTotalScreenshots}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="overflow-x-auto mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>Start Time</TableHead>
                  <TableHead>End Time</TableHead>
                  <TableHead>Tracked Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {timeLogs.map(timeLog => {
                  const project = projects.find(candidate => candidate.id === timeLog.project_id);
                  const startTime = new Date(timeLog.start_time);
                  const endTime = timeLog.end_time ? new Date(timeLog.end_time) : null;
                  const isOngoing = !timeLog.end_time;
                  const durationSeconds = timeLog.duration_seconds ?? calculateSessionSeconds(timeLog.start_time, timeLog.end_time ?? null);
                  const durationDisplay = formatDurationFromSeconds(durationSeconds);

                  return (
                    <TableRow key={timeLog.id} className={isOngoing ? 'bg-green-50 border-green-200' : ''}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <span>{project?.name || 'No Project'}</span>
                          {isOngoing && (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              <Clock className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{format(startTime, 'MMM dd, yyyy HH:mm')}</TableCell>
                      <TableCell>
                        {endTime ? format(endTime, 'MMM dd, yyyy HH:mm') : (
                          <Badge variant="outline" className="text-green-600">
                            Ongoing
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <span>{durationDisplay}</span>
                          {isOngoing && (
                            <RefreshCw className="h-3 w-3 text-green-600 animate-spin" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
